import { Injectable, inject } from '@angular/core';
import { WeathermapFacade } from '@store/weathermap.facade';
import { PinType, RainScale, TemperatureQuality } from '@store/weathermap.store';
import { firstValueFrom } from 'rxjs';
import { SelectedStationService } from 'src/app/services/selected-station.service';
import { MAP_CENTER_CACHE_KEY, MAP_ZOOM_CACHE_KEY, PIN_TYPE_CACHE_KEY, RAIN_SCALE_CACHE_KEY, STATION_ID_CACHE_KEY, TEMPERATURE_QUALITY_CACHE_KEY, UNIT_PRESSURE_CACHE_KEY, UNIT_SYSTEM_CACHE_KEY, UNIT_WIND_CACHE_KEY } from 'src/app/weathermap.const';

interface CacheConfig {
  pinType: PinType;
  rainScale: RainScale;
  temperatureQuality: TemperatureQuality;
  unitSystem: number;
  windUnit: number;
  pressureUnit: number;
  center: {lat: number, lng: number};
  zoom: number;
  stationId: string;
}

const DefaultCacheConfig: CacheConfig = {
  pinType: PinType.TEMPERATURE,
  rainScale: RainScale.RAIN_24H,
  temperatureQuality: TemperatureQuality.HIGH,
  unitSystem: 0,
  windUnit: 0,
  pressureUnit: 0,
  center: { lat: 48.7965913, lng: 2.3210938 },
  zoom: 3,
  stationId: null,
};

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  private readonly weathermapFacade = inject(WeathermapFacade);
  private readonly selectedStationService = inject(SelectedStationService);

  loadConfigFromCache(): CacheConfig {
    const pinType = localStorage.getItem(PIN_TYPE_CACHE_KEY) ?? DefaultCacheConfig.pinType;
    this.weathermapFacade.selectPinType(pinType as PinType);

    const rainScale = localStorage.getItem(RAIN_SCALE_CACHE_KEY) ?? DefaultCacheConfig.rainScale;
    this.weathermapFacade.selectRainScale(rainScale as RainScale);

    const temperatureQuality = localStorage.getItem(TEMPERATURE_QUALITY_CACHE_KEY) ?? DefaultCacheConfig.temperatureQuality;
    this.weathermapFacade.selectTemperatureQuality(temperatureQuality as TemperatureQuality);

    const unitSystem = localStorage.getItem(UNIT_SYSTEM_CACHE_KEY) ?? `${DefaultCacheConfig.unitSystem}`;
    this.weathermapFacade.selectUnitSystem(parseInt(unitSystem, 10));

    const windUnit = localStorage.getItem(UNIT_WIND_CACHE_KEY) ?? `${DefaultCacheConfig.windUnit}`;
    this.weathermapFacade.selectUnitWind(parseInt(windUnit, 10));

    const pressureUnit = localStorage.getItem(UNIT_PRESSURE_CACHE_KEY) ?? `${DefaultCacheConfig.pressureUnit}`;
    this.weathermapFacade.selectUnitPressure(parseInt(pressureUnit, 10));

    const centerCache = localStorage.getItem(MAP_CENTER_CACHE_KEY);
    const center = centerCache ? JSON.parse(centerCache) : DefaultCacheConfig.center;

    const zoomCache = localStorage.getItem(MAP_ZOOM_CACHE_KEY);
    const zoom = zoomCache ? JSON.parse(zoomCache) : DefaultCacheConfig.zoom;

    const stationIdCache = localStorage.getItem(STATION_ID_CACHE_KEY);
    const stationId = stationIdCache ? stationIdCache : DefaultCacheConfig.stationId;

    return {
      pinType: pinType as PinType,
      rainScale: rainScale as RainScale,
      temperatureQuality: temperatureQuality as TemperatureQuality,
      unitSystem: parseInt(unitSystem, 10),
      windUnit: parseInt(windUnit, 10),
      pressureUnit: parseInt(pressureUnit, 10),
      center,
      zoom,
      stationId,
    };
  }

  async loadStation(stationId: string): Promise<void> {
    if (stationId) {
      const loadStationSuccess = await firstValueFrom(this.weathermapFacade.loadStation(stationId));
      const coordinates = loadStationSuccess.payload.location;
      this.selectedStationService.selectStation(loadStationSuccess.payload, coordinates);
      window.map.setCenter([coordinates[0], coordinates[1]]);
    }
  }
}
